import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaExternalLinkAlt } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext';
import { uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';

const emptyInnovation = {
  title: '',
  summary: '',
  problem: '',
  solution: '',
  tech: [],
  image_url: '',
  development_story: '',
  impact: '',
  demo_link: '',
  visible: true
};

const InnovationsEditor = () => {
  const { portfolioData, loading, addInnovation, updateInnovation, deleteInnovation } = usePortfolio();
  const innovations = portfolioData?.innovations || [];
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingInnovation, setEditingInnovation] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newInnovation, setNewInnovation] = useState(emptyInnovation);

  const handleAddInnovation = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const innovation = {
        ...newInnovation,
        tech: Array.isArray(newInnovation.tech) ? newInnovation.tech : (newInnovation.tech ? newInnovation.tech.split(',').map(t => t.trim()) : []),
        visible: newInnovation.visible
      };
      const res = await addInnovation(innovation);
      if (res.success) {
        setNewInnovation(emptyInnovation);
        setShowAddForm(false);
        setMessage('Innovation added successfully!');
      } else {
        setMessage('Error adding innovation: ' + (res.error || ''));
      }
    } catch (error) {
      setMessage('Error adding innovation');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateInnovation = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const innovation = {
        ...editingInnovation,
        tech: Array.isArray(editingInnovation.tech) ? editingInnovation.tech : (editingInnovation.tech ? editingInnovation.tech.split(',').map(t => t.trim()) : []),
        visible: editingInnovation.visible
      };
      const res = await updateInnovation(editingInnovation.id, innovation);
      if (res.success) {
        setEditingInnovation(null);
        setMessage('Innovation updated successfully!');
      } else {
        setMessage('Error updating innovation: ' + (res.error || ''));
      }
    } catch (error) {
      setMessage('Error updating innovation');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteInnovation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this innovation?')) return;
    try {
      const res = await deleteInnovation(id);
      if (res.success) {
        setMessage('Innovation deleted successfully!');
      } else {
        setMessage('Error deleting innovation: ' + (res.error || ''));
      }
    } catch (error) {
      setMessage('Error deleting innovation');
    }
  };

  const handleImageUpload = async (file, innovationId = null) => {
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        const imageUrl = response.data.url;
        if (innovationId && editingInnovation) {
          setEditingInnovation({ ...editingInnovation, image_url: imageUrl });
        } else {
          setNewInnovation({ ...newInnovation, image_url: imageUrl });
        }
        setMessage('Image uploaded successfully!');
      }
    } catch (error) {
      setMessage('Error uploading image');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Innovations Manager</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <FaPlus />
          Add Innovation
        </motion.button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Add Innovation Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gray-700 rounded-lg p-6 mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-lg font-medium text-white">Add Innovation</h4>
            <button
              onClick={() => setShowAddForm(false)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded"
            >
              <FaTimes />
            </button>
          </div>
          <form onSubmit={e => { e.preventDefault(); handleAddInnovation(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
              <input type="text" value={newInnovation.title} onChange={e => setNewInnovation({ ...newInnovation, title: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Summary</label>
              <textarea value={newInnovation.summary} onChange={e => setNewInnovation({ ...newInnovation, summary: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Problem</label>
              <textarea value={newInnovation.problem} onChange={e => setNewInnovation({ ...newInnovation, problem: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Solution</label>
              <textarea value={newInnovation.solution} onChange={e => setNewInnovation({ ...newInnovation, solution: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Technologies (comma separated)</label>
              <input type="text" value={Array.isArray(newInnovation.tech) ? newInnovation.tech.join(', ') : newInnovation.tech} onChange={e => setNewInnovation({ ...newInnovation, tech: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Image</label>
              <div className="flex items-center gap-4">
                <input type="file" accept="image/*" onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file); }} className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
                {newInnovation.image_url && (
                  <div className="flex items-center gap-2">
                    <img src={newInnovation.image_url} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    <button type="button" onClick={() => setNewInnovation(prev => ({ ...prev, image_url: '' }))} className="p-1 text-red-400 hover:text-red-300"><FaTimes /></button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Development Story</label>
              <textarea value={newInnovation.development_story} onChange={e => setNewInnovation({ ...newInnovation, development_story: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Impact</label>
              <textarea value={newInnovation.impact} onChange={e => setNewInnovation({ ...newInnovation, impact: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Demo Link</label>
              <input type="text" value={newInnovation.demo_link} onChange={e => setNewInnovation({ ...newInnovation, demo_link: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" placeholder="https://..." />
            </div>
            <div className="flex items-center gap-2">
              <VisibilityToggle checked={newInnovation.visible ?? true} onChange={e => setNewInnovation({ ...newInnovation, visible: e.target.checked })} loading={false} />
            </div>
            <div className="flex gap-3 pt-4">
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Adding...' : 'Add Innovation'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowAddForm(false)} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Cancel
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Edit Innovation Form */}
      {editingInnovation && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gray-700 rounded-lg p-6 mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-lg font-medium text-white">Edit Innovation</h4>
            <button onClick={() => setEditingInnovation(null)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded"><FaTimes /></button>
          </div>
          <form onSubmit={e => { e.preventDefault(); handleUpdateInnovation(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
              <input type="text" value={editingInnovation.title} onChange={e => setEditingInnovation({ ...editingInnovation, title: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Summary</label>
              <textarea value={editingInnovation.summary} onChange={e => setEditingInnovation({ ...editingInnovation, summary: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Problem</label>
              <textarea value={editingInnovation.problem} onChange={e => setEditingInnovation({ ...editingInnovation, problem: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Solution</label>
              <textarea value={editingInnovation.solution} onChange={e => setEditingInnovation({ ...editingInnovation, solution: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Technologies (comma separated)</label>
              <input type="text" value={Array.isArray(editingInnovation.tech) ? editingInnovation.tech.join(', ') : editingInnovation.tech} onChange={e => setEditingInnovation({ ...editingInnovation, tech: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Image</label>
              <div className="flex items-center gap-4">
                <input type="file" accept="image/*" onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file, editingInnovation.id); }} className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
                {editingInnovation.image_url && (
                  <div className="flex items-center gap-2">
                    <img src={editingInnovation.image_url} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    <button type="button" onClick={() => setEditingInnovation(prev => ({ ...prev, image_url: '' }))} className="p-1 text-red-400 hover:text-red-300"><FaTimes /></button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Development Story</label>
              <textarea value={editingInnovation.development_story} onChange={e => setEditingInnovation({ ...editingInnovation, development_story: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Impact</label>
              <textarea value={editingInnovation.impact} onChange={e => setEditingInnovation({ ...editingInnovation, impact: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Demo Link</label>
              <input type="text" value={editingInnovation.demo_link} onChange={e => setEditingInnovation({ ...editingInnovation, demo_link: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" placeholder="https://..." />
            </div>
            <div className="flex items-center gap-2">
              <VisibilityToggle checked={editingInnovation.visible ?? true} onChange={e => setEditingInnovation({ ...editingInnovation, visible: e.target.checked })} loading={false} />
            </div>
            <div className="flex gap-3 pt-4">
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setEditingInnovation(null)} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Cancel
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Innovations List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {innovations.map((innovation, idx) => (
          <div key={innovation.id || idx} className="bg-gray-700 rounded p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold text-white flex-1">{innovation.title}</h4>
              <button onClick={() => setEditingInnovation(innovation)} className="text-yellow-400 hover:text-yellow-300"><FaEdit /></button>
              <button onClick={() => handleDeleteInnovation(innovation.id)} className="text-red-400 hover:text-red-300"><FaTrash /></button>
            </div>
            <div className="text-gray-400 text-xs mb-2">{innovation.summary}</div>
            <div className="mb-2">
              {innovation.image_url && <img src={innovation.image_url} alt={innovation.title} className="w-full h-32 object-cover rounded mb-2" />}
              <div className="flex flex-wrap gap-2 mb-2">
                {(innovation.tech || []).map((tech, i) => (
                  <span key={i} className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">{tech}</span>
                ))}
              </div>
              <div className="text-gray-300 text-sm mb-2">{innovation.problem && innovation.problem.slice(0, 80)}...</div>
              <div className="text-gray-300 text-sm mb-2">{innovation.solution && innovation.solution.slice(0, 80)}...</div>
              <div className="flex items-center gap-2">
                <VisibilityToggle checked={innovation.visible ?? true} onChange={e => { const updatedInnovation = { ...innovation, visible: e.target.checked }; updateInnovation(innovation.id, updatedInnovation); }} loading={false} />
                {innovation.demo_link && (
                  <a href={innovation.demo_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1 ml-auto"><FaExternalLinkAlt /> Demo</a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default InnovationsEditor; 